import React from 'react';
import {
    VStack,
    HStack,
    Text,
    Avatar,
    Box,
    Circle,
    Icon,
    IconButton,
    Badge,
    Spinner,
    Grid,
    GridItem,
    Tooltip,
    Button,
    StackDivider,
} from '@chakra-ui/react';
import { FiMic, FiPhone, FiVideo, FiMoreVertical } from 'react-icons/fi';
import { HiSpeakerWave, HiSignal } from 'react-icons/hi2';
import { CallUpsertViewProps } from './callUpsertProps';
import { IntegrationViewBaseConfig } from '../../../base';

// Simple status display component for non-connected states
const StatusDisplay: React.FC<
    Pick<CallUpsertViewProps, 'status'> & {
        showSpinner?: boolean;
        children?: React.ReactNode;
    }
> = ({ status, showSpinner = false, children }) => (
    <VStack spacing={6} align="center" justify="center" h="72">
        <Circle size="16" bg={`${status.colorScheme}.500`} color="white">
            <Icon as={status.Icon} boxSize={6} />
        </Circle>

        <VStack spacing={2} align="center">
            <Text fontSize="lg" fontWeight="semibold" color="white">
                {status.label}
            </Text>
            <Text fontSize="sm" color="whiteAlpha.700">
                {status.description}
            </Text>
        </VStack>

        {showSpinner && <Spinner size="md" color={`${status.colorScheme}.500`} />}

        <Badge
            colorScheme={status.colorScheme}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            variant="solid"
        >
            {status.label}
        </Badge>

        {children}
    </VStack>
);

export function createCallUpsertView(
    config: IntegrationViewBaseConfig
): React.FC<CallUpsertViewProps> {
    return (props) => {
        // Render connected state (similar to callDetailView)
        if (props.status.kind === 'connected') {
            return (
                <VStack
                    spacing={0}
                    align="start"
                    h="full"
                    color="whiteAlpha.900"
                    w="full"
                >
                    <VStack
                        w="full"
                        align="start"
                        spacing={0}
                        divider={
                            <StackDivider borderWidth={1} borderColor="whiteAlpha.200" />
                        }
                    >
                        {/* Header with title and call duration */}
                        <HStack
                            justify="space-between"
                            align="center"
                            w="full"
                            px={6}
                            py={3}
                        >
                            <Text fontSize="lg" fontWeight="medium">
                                {props.title}
                            </Text>
                            <Text
                                fontSize="md"
                                color="whiteAlpha.700"
                                fontWeight="medium"
                            >
                                {props.callDuration}
                            </Text>
                        </HStack>
                        {/* Main Participant Area */}
                        <Grid
                            p={6}
                            templateColumns={
                                props.participants.length === 1
                                    ? '1fr'
                                    : props.participants.length === 2
                                      ? '1fr 1fr'
                                      : props.participants.length === 3
                                        ? '1fr 1fr 1fr'
                                        : '1fr 1fr'
                            }
                            templateRows={
                                props.participants.length > 4 ? '1fr 1fr' : '1fr'
                            }
                            gap={4}
                            w="full"
                        >
                            {props.participants.map((participant, index) => (
                                <GridItem key={participant.kind}>
                                    <VStack
                                        bg="whiteAlpha.100"
                                        borderRadius="xl"
                                        p={6}
                                        transition="all 0.3s ease"
                                        _hover={{
                                            bg: 'whiteAlpha.150',
                                        }}
                                        position="relative"
                                        minH="12rem"
                                        spacing={4}
                                        justify="center"
                                        align="center"
                                    >
                                        <Box position="relative">
                                            <Avatar
                                                size="lg"
                                                name={participant.label}
                                                src={participant.avatar}
                                                borderWidth={participant.speaking ? 2 : 2}
                                                borderColor={
                                                    participant.speaking
                                                        ? 'green.400'
                                                        : 'whiteAlpha.400'
                                                }
                                                transition="all 0.3s ease"
                                            />
                                            {/* Speaking indicator */}
                                            {participant.speaking && (
                                                <Box
                                                    position="absolute"
                                                    top="-0.5"
                                                    right="-0.5"
                                                    bg="green.400"
                                                    borderRadius="full"
                                                    p={1}
                                                    animation="pulse 1s infinite"
                                                >
                                                    <Icon
                                                        as={HiSpeakerWave}
                                                        boxSize={3}
                                                        color="white"
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Participant name and microphone indicator */}
                                        <Box
                                            position="absolute"
                                            bottom={3}
                                            left={3}
                                            bg="blackAlpha.600"
                                            borderRadius="md"
                                            px={2}
                                            py={1}
                                        >
                                            <HStack spacing={2} align="center">
                                                <Icon
                                                    as={FiMic}
                                                    boxSize={3}
                                                    color="white"
                                                />
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="medium"
                                                    color="white"
                                                    noOfLines={1}
                                                >
                                                    {participant.label}
                                                </Text>
                                            </HStack>
                                        </Box>
                                    </VStack>
                                </GridItem>
                            ))}
                        </Grid>
                    </VStack>

                    {/* Call Controls */}
                    <HStack w="full" justify="center" px={6} pb={6}>
                        <HStack spacing={3}>
                            {props.action.items.map((action, index) => {
                                return (
                                    <Tooltip
                                        key={action.id}
                                        label={action.label}
                                        placement="top"
                                        bg="gray.50"
                                        color="gray.900"
                                        openDelay={300}
                                        py={1}
                                        px={2}
                                        borderRadius="md"
                                        shouldWrapChildren={true}
                                    >
                                        <IconButton
                                            {...action.button}
                                            aria-label={action.label}
                                            icon={
                                                action.Icon ? (
                                                    <Icon
                                                        as={action.Icon}
                                                        fontSize="lg"
                                                    />
                                                ) : undefined
                                            }
                                            borderRadius="lg"
                                            bg={
                                                action.button.colorScheme
                                                    ? `${action.button.colorScheme}.500`
                                                    : 'whiteAlpha.200'
                                            }
                                            color="whiteAlpha.800"
                                            w={10}
                                            minW={0}
                                            h={10}
                                            minH={0}
                                            _hover={{
                                                bg: action.button.colorScheme
                                                    ? `${action.button.colorScheme}.600`
                                                    : 'whiteAlpha.300',
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </HStack>
                    </HStack>
                </VStack>
            );
        }

        if (props.status.kind === 'connecting') {
            return (
                <VStack
                    spacing={0}
                    align="start"
                    h="full"
                    color="whiteAlpha.900"
                    w="full"
                >
                    <VStack
                        w="full"
                        align="start"
                        spacing={0}
                        divider={
                            <StackDivider borderWidth={1} borderColor="whiteAlpha.200" />
                        }
                    >
                        {/* Header with title and call duration */}
                        <HStack
                            justify="space-between"
                            align="center"
                            w="full"
                            px={6}
                            py={3}
                        >
                            <Text fontSize="lg" fontWeight="medium">
                                {props.title}
                            </Text>
                            <Text
                                fontSize="md"
                                color="whiteAlpha.700"
                                fontWeight="medium"
                            >
                                {props.callDuration}
                            </Text>
                        </HStack>

                        {/* Connecting State Content */}
                        <HStack
                            spacing={6}
                            align="center"
                            justify="center"
                            w="full"
                            flex={1}
                            p={6}
                            color="whiteAlpha.600"
                        >
                            <VStack spacing={2} align="center">
                                <Text fontSize="md" fontWeight="medium">
                                    {props.status.description}
                                </Text>
                            </VStack>
                            <Spinner size="md" speed="2s" />
                        </HStack>
                    </VStack>
                </VStack>
            );
        }

        // Render ended state
        if (props.status.kind === 'ended') {
            return <StatusDisplay status={props.status} />;
        }

        // Render error state
        if (props.status.kind === 'error') {
            return <StatusDisplay status={props.status} />;
        }

        // Default/fallback state
        return <StatusDisplay status={props.status} showSpinner={true} />;
    };
}
